import { useTranslation } from "react-i18next";
import { CommentSubjectKind, MarketDialogueAnswerType, MarketDialogueBlocker } from "../../autogen/bff-api";

export const ORGANIZATION_PROFILE_BLOCKERS: readonly MarketDialogueBlocker[] = [
  MarketDialogueBlocker.BuyerLegalTypeTooGeneral,
  MarketDialogueBlocker.BuyerOrganizationNumberMissing,
  MarketDialogueBlocker.BuyerPostCodeMissing,
  MarketDialogueBlocker.ContactPhoneMissing,
];

export const useMarketDialogueBlockerLabel = (canAnnounceOnDoffin: boolean) => {
  const { t } = useTranslation();
  return (blocker: MarketDialogueBlocker): string => {
    switch (blocker) {
      case MarketDialogueBlocker.TitleMissing:
        return t("Give the market dialogue a title.");
      case MarketDialogueBlocker.DescriptionMissing:
        return t("Describe the planned procurement.");
      case MarketDialogueBlocker.QuestionsMissing:
        return t("Add at least one question to the suppliers.");
      case MarketDialogueBlocker.AnswerDeadlineMissing:
        return t("Set an answer deadline.");
      case MarketDialogueBlocker.AnswerDeadlinePassed:
        return t("The answer deadline has passed. Choose a later one.");
      case MarketDialogueBlocker.ExpectedContractNoticeDateMissing:
        return t(
          "Say when you expect to announce the competition. Doffin requires it in a prior information notice under FOA above the EEA threshold.",
        );
      case MarketDialogueBlocker.ExpectedContractNoticeDateNotInFuture:
        return t("The date you expect to announce the competition must be after today.");
      case MarketDialogueBlocker.RegulationMissing:
        return t("Choose the regulation the procurement falls under.");
      case MarketDialogueBlocker.ContractNatureMissing:
        return t("Choose what the procurement concerns.");
      case MarketDialogueBlocker.ServiceCategoryMissing:
        return t("Choose what kind of service the contract is.");
      case MarketDialogueBlocker.EstimatedValueMissing:
        return t("Enter the estimated value.");
      case MarketDialogueBlocker.CpvCodeMissing:
        return t("Choose at least one CPV code.");
      case MarketDialogueBlocker.UtilityActivityMissing:
        return t("Choose the utility activity the procurement serves.");
      case MarketDialogueBlocker.BuyerLegalTypeMissing:
        return t("Your organisation has no «Buyer legal type». SourceMagnet sets it: ask support through «Help» in the menu.");
      case MarketDialogueBlocker.BuyerLegalTypeTooGeneral:
        return t(
          "Doffin needs to know who controls your organisation. An administrator chooses it under «Edit company profile», «Procurements», «Buyer legal type».",
        );
      case MarketDialogueBlocker.OutsideMarketDialogueScope:
        return t(
          "A market dialogue can be announced for procurements under FOA Part II and Part III and under the utilities regulation, but not for health, social and education services under FOA. Check the estimated value and the kind of service.",
        );
      case MarketDialogueBlocker.BuyerOrganizationNumberMissing:
        return t("Your organisation's organisation number is missing from its profile.");
      case MarketDialogueBlocker.BuyerPostCodeMissing:
        return t("Your organisation's postcode is missing from its profile.");
      case MarketDialogueBlocker.ContactPhoneMissing:
        return t("Your organisation's phone number is missing from its profile.");
      case MarketDialogueBlocker.InvitationsMissing:
        return canAnnounceOnDoffin
          ? t("Invite at least one supplier, or announce the market dialogue on Doffin.")
          : t("Invite at least one supplier.");
    }
  };
};

export const useNoticeTypeLabel = () => {
  const { t } = useTranslation();
  return (noticeSubType: string): string => {
    switch (noticeSubType) {
      case "E2":
        return t("Prior information notice below the EEA threshold");
      case "4":
        return t("Prior information notice above the EEA threshold");
      case "5":
        return t("Prior information notice above the EEA threshold (utilities regulation)");
      default:
        return noticeSubType;
    }
  };
};

export const MARKET_DIALOGUE_ANSWER_TYPES: readonly MarketDialogueAnswerType[] = [
  MarketDialogueAnswerType.FreeText,
  MarketDialogueAnswerType.YesNoComment,
  MarketDialogueAnswerType.Document,
];

export const useAnswerTypeLabel = () => {
  const { t } = useTranslation();
  return (answerType: MarketDialogueAnswerType): string => {
    switch (answerType) {
      case MarketDialogueAnswerType.FreeText:
        return t("Free text");
      case MarketDialogueAnswerType.YesNoComment:
        return t("Yes/no with comment");
      case MarketDialogueAnswerType.Document:
        return t("Document");
    }
  };
};

export const answerTypeOf = (question: { answerType?: MarketDialogueAnswerType }): MarketDialogueAnswerType =>
  question.answerType ?? MarketDialogueAnswerType.FreeText;

export const takesDocuments = (question: { answerType?: MarketDialogueAnswerType; allowsAttachments?: boolean }) =>
  answerTypeOf(question) === MarketDialogueAnswerType.Document || (question.allowsAttachments ?? true);

export const useCommentSubjectKindLabel = () => {
  const { t } = useTranslation();
  return (kind: CommentSubjectKind): string => {
    switch (kind) {
      case CommentSubjectKind.MinimumRequirement:
        return t("Minimum requirement");
      case CommentSubjectKind.QualificationRequirement:
        return t("Qualification requirement");
      case CommentSubjectKind.AwardCriterion:
        return t("Award criterion");
      case CommentSubjectKind.PriceForm:
        return t("Price form");
      case CommentSubjectKind.Document:
        return t("Document");
    }
  };
};
